import { router } from '@inertiajs/react';
import confetti from 'canvas-confetti';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LogoGoogle } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { googleSignInPath } from '@/pages/Votes/components/google';

interface ArcadeVoteButtonProps {
  gameId: string;
  gameSlug: string;
  gameTitle: string;
  voted: boolean;
  count: number;
  isAuthenticated: boolean;
  hasGoogleAccount: boolean;
  votingOpen: boolean;
  compact?: boolean;
  className?: string;
}

const CONFETTI_COLORS = ['#e1ff00', '#a8c700', '#f7ffd8', '#ffffff', '#6b7f14'];

function celebrate() {
  // Left burst
  confetti({
    particleCount: 120,
    angle: 60,
    spread: 70,
    origin: { x: 0, y: 0.7 },
    colors: CONFETTI_COLORS,
    gravity: 0.8,
    startVelocity: 55,
    decay: 0.94,
    ticks: 250,
  });
  // Right burst
  confetti({
    particleCount: 120,
    angle: 120,
    spread: 70,
    origin: { x: 1, y: 0.7 },
    colors: CONFETTI_COLORS,
    gravity: 0.8,
    startVelocity: 55,
    decay: 0.94,
    ticks: 250,
  });
  // Delayed center shower
  setTimeout(() => {
    confetti({
      particleCount: 80,
      angle: 90,
      spread: 120,
      origin: { x: 0.5, y: 0.3 },
      colors: CONFETTI_COLORS,
      gravity: 1.2,
      startVelocity: 40,
      decay: 0.92,
      ticks: 100,
    });
  }, 150);
}

export function ArcadeVoteButton({
  gameId,
  gameSlug,
  gameTitle,
  voted,
  count,
  isAuthenticated,
  hasGoogleAccount,
  votingOpen,
  compact: _compact = false,
  className,
}: ArcadeVoteButtonProps) {
  const [isPending, setIsPending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const wasVoted = useRef(voted);

  // The vote is a redirect back to this page, so the fresh tally arrives as props.
  // Celebrate when it flips from "not voted" to "voted", as the server action did.
  useEffect(() => {
    if (voted && !wasVoted.current) {
      celebrate();
    }
    wasVoted.current = voted;
  }, [voted]);

  const handleVote = () => {
    if (!isAuthenticated || !hasGoogleAccount) {
      setShowModal(true);
      return;
    }

    if (!votingOpen) {
      toast.error('La votación no está disponible en este momento.');
      return;
    }

    router.post(
      '/26/arcade/vote',
      { gameId },
      {
        preserveScroll: true,
        onStart: () => setIsPending(true),
        onFinish: () => setIsPending(false),
        onError: (errors) => {
          if (errors.voteRequiresAuth) {
            setShowModal(true);
          }
        },
      },
    );
  };

  const handleGoogleLogin = () => {
    window.location.href = googleSignInPath(`/26/arcade/${gameSlug}`);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleVote}
        disabled={isPending}
        className={cn(
          'flex items-center gap-2.5 rounded-md border-2 px-5 py-2.5 font-bold font-mono text-base transition-all',
          voted
            ? 'border-primary/40 bg-primary/15 text-primary'
            : 'animate-pulse-subtle border-primary/50 bg-primary/20 text-primary hover:border-primary/70 hover:bg-primary/30',
          isPending && 'opacity-60',
          className,
        )}
      >
        <span aria-hidden="true" className="text-lg">
          ▲
        </span>
        <span>{count}</span>
      </button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <div className="mb-2 flex items-center justify-center">
              <LogoGoogle size={48} />
            </div>
            <DialogTitle className="text-center font-title">
              Inicio de sesión con Google requerido
            </DialogTitle>
            <DialogDescription className="text-center font-title">
              Necesitas iniciar sesión con una cuenta de Google para votar por{' '}
              {gameTitle}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-4">
            <Button onClick={handleGoogleLogin} className="w-full">
              <LogoGoogle size={20} />
              Iniciar sesión con Google
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              className="w-full"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
